import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import {
  Award,
  Plus,
  CalendarDays,
  Pencil,
  Trash2,
  Save,
  X,
  Star,
  Flame,
  Heart,
  ThumbsUp,
  ImagePlus,
  Eye,
} from "lucide-react";
import { API_URL } from "../../api/api";
import { UPLOAD_URL } from "../../api/api";
import {
  Page,
  PageHeader,
  Card,
  PrimaryButton,
  SecondaryButton,
  Chip,
  Label,
  Input,
  Select,
  EmptyState,
  ModalOverlay,
} from "../../components/em/ui";
import { T } from "../../components/em/tokens";

import { confirmDialog } from "../../components/em/confirm";
import { toast } from "sonner";
const STARS_BASE = `${API_URL}/api/star-of-month`;
const TEAM_BASE = `${API_URL}/api/settings/shifts/team-members`;

// Default badge list — overwritten by /meta on mount.
const DEFAULT_BADGES = [
  "Perfect Attendance",
  "Top Performer",
  "Customer Champion",
  "Team Player",
  "Innovator",
  "Most Helpful",
  "Rising Star",
  "Other",
];

const monthLabel = (mm) => {
  if (!mm) return "—";
  const [y, m] = String(mm).split("-").map(Number);
  if (!y || !m) return mm;
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
};

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const StarOfMonthManagement = () => {
  const [meta, setMeta] = useState({
    badges: DEFAULT_BADGES,
    max_per_month: 6,
  });
  const [employees, setEmployees] = useState([]);
  const [history, setHistory] = useState([]); // [{ month, stars: [...] }]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // star object when editing
  const [busy, setBusy] = useState(false);

  // Dashboard visibility window. `visibleDays` is the saved value;
  // `visibleDaysDraft` lets the user edit before saving.
  const [visibleDays, setVisibleDays] = useState(10);
  const [visibleDaysDraft, setVisibleDaysDraft] = useState(10);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsStatus, setSettingsStatus] = useState(null);

  // Form state
  const blankForm = {
    month: currentMonth(),
    employee_id: "",
    badge: "Top Performer",
    badge_custom: "",
    reason: "",
    photo: null,
    photoPreview: null,
  };
  const [form, setForm] = useState(blankForm);

  /* ---- data fetchers ---- */
  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${STARS_BASE}/history`);
      setHistory(r.data?.months || []);
      setError(null);
    } catch (e) {
      setError(
        e?.response?.data?.error || "Could not load Star of the Month list.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    axios.get(`${STARS_BASE}/meta`).then(
      (r) => {
        setMeta(r.data || { badges: DEFAULT_BADGES, max_per_month: 6 });
        const d = Number(r.data?.dashboard_visible_days);
        if (Number.isFinite(d)) {
          setVisibleDays(d);
          setVisibleDaysDraft(d);
        }
      },
      () => {},
    );
    axios.get(TEAM_BASE).then(
      (r) => setEmployees(r.data || []),
      () => setEmployees([]),
    );
    fetchHistory();
  }, [fetchHistory]);

  const saveVisibleDays = async () => {
    const n = Math.max(1, Math.min(31, Number(visibleDaysDraft) || 0));
    setSavingSettings(true);
    setSettingsStatus(null);
    try {
      await axios.put(`${STARS_BASE}/settings`, {
        dashboard_visible_days: n,
      });
      setVisibleDays(n);
      setVisibleDaysDraft(n);
      setSettingsStatus({
        kind: "ok",
        text: `Saved — widget will show on days 1–${n} of each month.`,
      });
      setTimeout(() => setSettingsStatus(null), 3000);
    } catch (err) {
      setSettingsStatus({
        kind: "err",
        text: err?.response?.data?.error || "Could not save setting.",
      });
    } finally {
      setSavingSettings(false);
    }
  };

  /* ---- modal open/close ---- */
  const openCreate = () => {
    setEditing(null);
    setForm(blankForm);
    setShowForm(true);
  };
  const openEdit = (star) => {
    setEditing(star);
    setForm({
      month: star.month,
      employee_id: star.employee_id,
      badge: star.badge || "Top Performer",
      badge_custom: star.badge_custom || "",
      reason: star.reason || "",
      photo: null,
      photoPreview: null,
    });
    setShowForm(true);
  };
  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(blankForm);
  };

  /* ---- form helpers ---- */
  const onPhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo must be under 5 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () =>
      setForm((f) => ({ ...f, photo: file, photoPreview: reader.result }));
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      if (!editing) {
        fd.append("month", form.month);
        fd.append("employee_id", String(form.employee_id));
      }
      fd.append("badge", form.badge);
      if (form.badge === "Other")
        fd.append("badge_custom", form.badge_custom || "");
      if (form.reason) fd.append("reason", form.reason);
      if (form.photo) fd.append("photo", form.photo);

      if (editing) {
        await axios.put(`${STARS_BASE}/${editing.id}`, fd);
      } else {
        if (!form.employee_id) {
          toast.error("Please select an employee.");
          setBusy(false);
          return;
        }
        await axios.post(`${STARS_BASE}`, fd);
      }
      await fetchHistory();
      closeForm();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (star) => {
    if (!(await confirmDialog(
      `Remove ${star.name} as Star of the Month for ${monthLabel(star.month)}?`
    )))
      return;
    try {
      await axios.delete(`${STARS_BASE}/${star.id}`);
      fetchHistory();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not remove.");
    }
  };

  /* ---- helper for photo url ---- */
  const photoUrl = (star) => {
    const p = star.photo_override || star.profile_photo;
    if (!p) return null;
    if (/^https?:\/\//.test(p)) return p;
    return `${UPLOAD_URL}${p.startsWith("/") ? "" : "/"}${p}`;
  };

  const totalStars = useMemo(
    () => history.reduce((acc, m) => acc + (m.stars?.length || 0), 0),
    [history],
  );

  return (
    <Page>
      <PageHeader
        icon={Award}
        title="Star of the Month"
        subtitle={`Recognise outstanding employees — up to ${meta.max_per_month || 6} stars per month.${totalStars > 0 ? ` ${totalStars} awarded so far.` : ""}`}
        actions={
          <PrimaryButton icon={Plus} onClick={openCreate}>
            Award a Star
          </PrimaryButton>
        }
      />

      {/* Dashboard visibility window */}
      <Card style={{ marginBottom: 18 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: T.rMd,
              flexShrink: 0,
              background: T.orange50,
              border: `1px solid ${T.orangeEdge}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Eye size={18} color={T.orange} />
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div
              style={{
                font: `600 13.5px ${T.font}`,
                color: T.fg1,
                marginBottom: 3,
              }}
            >
              Dashboard widget visibility
            </div>
            <div style={{ font: `400 12.5px/1.5 ${T.font}`, color: T.fg5 }}>
              The Star of the Month widget appears on the dashboard for the
              first <b>{visibleDays}</b> day{visibleDays === 1 ? "" : "s"} of
              each month, then hides until the next month begins.
            </div>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <label
              htmlFor="vis-days"
              style={{ font: `500 12.5px ${T.font}`, color: T.fg5 }}
            >
              Show for first
            </label>
            <Input
              id="vis-days"
              type="number"
              min={1}
              max={31}
              value={visibleDaysDraft}
              onChange={(e) => setVisibleDaysDraft(e.target.value)}
              style={{ width: 72, textAlign: "center" }}
            />
            <span style={{ font: `500 12.5px ${T.font}`, color: T.fg5 }}>
              day{Number(visibleDaysDraft) === 1 ? "" : "s"}
            </span>
            <PrimaryButton
              icon={Save}
              onClick={saveVisibleDays}
              disabled={
                savingSettings ||
                Number(visibleDaysDraft) === Number(visibleDays) ||
                !Number.isFinite(Number(visibleDaysDraft)) ||
                Number(visibleDaysDraft) < 1 ||
                Number(visibleDaysDraft) > 31
              }
            >
              {savingSettings ? "Saving…" : "Save"}
            </PrimaryButton>
          </div>
          {settingsStatus && (
            <div
              role="status"
              style={{
                flexBasis: "100%",
                padding: "9px 13px",
                borderRadius: T.rMd,
                font: `500 12.5px ${T.font}`,
                background:
                  settingsStatus.kind === "ok" ? "#ecfdf5" : T.danger50,
                color: settingsStatus.kind === "ok" ? "#047857" : T.danger,
                border: `1px solid ${settingsStatus.kind === "ok" ? "#a7f3d0" : "rgba(231,0,11,0.2)"}`,
              }}
            >
              {settingsStatus.kind === "ok" ? "✓ " : "⚠ "}
              {settingsStatus.text}
            </div>
          )}
        </div>
      </Card>

      {error && (
        <div
          role="alert"
          style={{
            margin: "12px 0",
            padding: "10px 14px",
            borderRadius: T.rMd,
            border: "1px solid rgba(231,0,11,0.2)",
            background: T.danger50,
            color: T.danger,
            font: `500 13px ${T.font}`,
          }}
        >
          ⚠ {error}
        </div>
      )}

      {loading ? (
        <div
          style={{
            padding: 32,
            textAlign: "center",
            font: `400 13px ${T.font}`,
            color: T.fg5,
          }}
        >
          Loading Star of the Month records…
        </div>
      ) : history.length === 0 ? (
        <EmptyState
          icon={Star}
          title="No stars awarded yet"
          subtitle="Click Award a Star to recognise an employee."
          action={
            <PrimaryButton icon={Plus} onClick={openCreate}>
              Award a Star
            </PrimaryButton>
          }
        />
      ) : (
        history.map((monthGroup) => (
          <Card
            key={monthGroup.month}
            padding={0}
            style={{ marginBottom: 20, overflow: "hidden" }}
          >
            <div
              style={{
                padding: "13px 20px",
                borderBottom: `1px solid ${T.divider}`,
                display: "flex",
                alignItems: "center",
                gap: 9,
                background: T.bgHead,
              }}
            >
              <CalendarDays size={16} color={T.orange} />
              <strong style={{ font: `600 14px ${T.font}`, color: T.fg1 }}>
                {monthLabel(monthGroup.month)}
              </strong>
              <Chip>
                {monthGroup.stars.length} star
                {monthGroup.stars.length === 1 ? "" : "s"}
              </Chip>
            </div>
            <div
              style={{
                padding: 18,
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))",
                gap: 14,
              }}
            >
              {monthGroup.stars.map((star) => (
                <StarCard
                  key={star.id}
                  star={star}
                  photoUrl={photoUrl(star)}
                  onEdit={() => openEdit(star)}
                  onDelete={() => remove(star)}
                />
              ))}
            </div>
          </Card>
        ))
      )}

      {showForm && (
        <StarFormModal
          editing={editing}
          form={form}
          setForm={setForm}
          employees={employees}
          badges={meta.badges || DEFAULT_BADGES}
          busy={busy}
          onClose={closeForm}
          onSubmit={submit}
          onPhotoChange={onPhotoChange}
        />
      )}
    </Page>
  );
};

/* ---------------- card ---------------- */
const cardBtn = (danger) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  background: "var(--em-bg-card)",
  border: `1px solid ${danger ? "rgba(231,0,11,0.25)" : T.border}`,
  color: danger ? T.danger : T.fg4,
  padding: "5px 11px",
  borderRadius: T.rSm,
  cursor: "pointer",
  font: `500 12px ${T.font}`,
});

const StarCard = ({ star, photoUrl, onEdit, onDelete }) => (
  <div
    style={{
      border: `1px solid ${T.border}`,
      borderRadius: T.rLg,
      padding: 14,
      display: "flex",
      gap: 13,
      alignItems: "flex-start",
      background: T.bgCard,
      boxShadow: T.shadowSm,
    }}
  >
    <div
      style={{
        width: 64,
        height: 64,
        borderRadius: T.rMd,
        background: photoUrl
          ? `${T.bgHead} url(${photoUrl}) center/cover no-repeat`
          : "linear-gradient(135deg,#ff6900,#f54900)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontWeight: 700,
        fontSize: 20,
        flexShrink: 0,
        border: `1px solid ${T.border}`,
      }}
    >
      {!photoUrl && (star.name?.[0] || "?")}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          font: `600 14px ${T.font}`,
          color: T.fg1,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {star.name}
      </div>
      <div
        style={{ font: `400 12px ${T.font}`, color: T.fg5, marginBottom: 7 }}
      >
        {[star.designation, star.department].filter(Boolean).join(" · ") || "—"}
      </div>
      <Chip
        bg={T.orange50}
        color="#c2410c"
        border={T.orangeEdge}
        style={{ marginBottom: 8 }}
      >
        <Star size={11} style={{ marginRight: 4 }} />
        {star.badge === "Other" ? star.badge_custom || "Other" : star.badge}
      </Chip>
      {star.reason && (
        <div
          style={{
            font: `400 12px/1.5 ${T.font}`,
            color: T.fg4,
            margin: "8px 0",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {star.reason}
        </div>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 9,
          marginTop: 8,
          font: `500 11.5px ${T.font}`,
          color: T.fg5,
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
          <Flame size={12} color="#f97316" /> {star.fire_count}
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
          <Heart size={12} color="#e7000b" /> {star.heart_count}
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
          <ThumbsUp size={12} color="#2b7fff" /> {star.thumb_count}
        </span>
      </div>
      <div
        style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}
      >
        <button type="button" onClick={onEdit} style={cardBtn(false)}>
          <Pencil size={12} /> Edit
        </button>
        <button type="button" onClick={onDelete} style={cardBtn(true)}>
          <Trash2 size={12} /> Remove
        </button>
      </div>
    </div>
  </div>
);
StarCard.propTypes = {
  star: PropTypes.object.isRequired,
  photoUrl: PropTypes.string,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

/* ---------------- modal ---------------- */
const StarFormModal = ({
  editing,
  form,
  setForm,
  employees,
  badges,
  busy,
  onClose,
  onSubmit,
  onPhotoChange,
}) => (
  <ModalOverlay onClose={onClose}>
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: "var(--em-bg-card)",
        borderRadius: T.rXl,
        padding: 24,
        maxWidth: 540,
        width: "100%",
        maxHeight: "90vh",
        overflowY: "auto",
        boxShadow: T.shadowLg || "0 20px 50px rgba(0,0,0,0.25)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 18,
        }}
      >
        <span
          style={{
            width: 36,
            height: 36,
            borderRadius: T.rMd,
            flexShrink: 0,
            background: T.orange50,
            border: `1px solid ${T.orangeEdge}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Award size={18} color={T.orange} />
        </span>
        <h3
          style={{
            margin: 0,
            font: `700 17px ${T.font}`,
            color: T.fg1,
            flex: 1,
          }}
        >
          {editing ? "Edit Star" : "Award a Star"}
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            width: 30,
            height: 30,
            borderRadius: T.rSm,
            cursor: "pointer",
            background: "transparent",
            border: `1px solid ${T.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: T.fg5,
          }}
        >
          <X size={15} />
        </button>
      </div>

      <div style={{ marginBottom: 13 }}>
        <Label
          hint={
            editing
              ? "Month cannot be changed for an existing star."
              : undefined
          }
        >
          Month
        </Label>
        <Input
          type="month"
          value={form.month}
          disabled={!!editing}
          onChange={(e) => setForm((f) => ({ ...f, month: e.target.value }))}
        />
      </div>

      <div style={{ marginBottom: 13 }}>
        <Label
          hint={
            editing
              ? "Employee cannot be changed for an existing star."
              : undefined
          }
        >
          Employee
        </Label>
        <Select
          value={form.employee_id}
          disabled={!!editing}
          onChange={(e) =>
            setForm((f) => ({ ...f, employee_id: e.target.value }))
          }
        >
          <option value="">— select an employee —</option>
          {employees.map((m) => {
            const name =
              `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.email;
            return (
              <option key={m.id} value={m.id}>
                {name}
                {m.designation ? ` · ${m.designation}` : ""}
              </option>
            );
          })}
        </Select>
      </div>

      <div style={{ marginBottom: 13 }}>
        <Label>Badge</Label>
        <Select
          value={form.badge}
          onChange={(e) => setForm((f) => ({ ...f, badge: e.target.value }))}
        >
          {badges.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </Select>
      </div>

      {form.badge === "Other" && (
        <div style={{ marginBottom: 13 }}>
          <Label>Custom badge text</Label>
          <Input
            type="text"
            value={form.badge_custom}
            placeholder="e.g. Hackathon Winner"
            onChange={(e) =>
              setForm((f) => ({ ...f, badge_custom: e.target.value }))
            }
          />
        </div>
      )}

      <div style={{ marginBottom: 13 }}>
        <Label>Reason / citation (optional, max 500 chars)</Label>
        <textarea
          rows={3}
          maxLength={500}
          value={form.reason}
          placeholder="Shipped the new onboarding flow ahead of schedule…"
          onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "9px 12px",
            border: `1px solid ${T.border}`,
            borderRadius: T.rMd,
            font: `400 13px/1.5 ${T.font}`,
            color: T.fg1,
            resize: "vertical",
            outline: "none",
          }}
        />
      </div>

      <div style={{ marginBottom: 18 }}>
        <Label hint="Falls back to the employee's profile photo if not provided. Max 5 MB.">
          Photo override (optional)
        </Label>
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            cursor: "pointer",
            padding: "8px 13px",
            borderRadius: T.rMd,
            border: `1px dashed ${T.border}`,
            background: T.bgHead,
            font: `500 12.5px ${T.font}`,
            color: T.fg4,
          }}
        >
          <ImagePlus size={15} color={T.orange} />
          Choose image…
          <input
            type="file"
            accept="image/*"
            onChange={onPhotoChange}
            style={{ display: "none" }}
          />
        </label>
        {form.photoPreview && (
          <div style={{ marginTop: 10 }}>
            <img
              src={form.photoPreview}
              alt="preview"
              style={{
                width: 96,
                height: 96,
                borderRadius: T.rMd,
                objectFit: "cover",
                border: `1px solid ${T.border}`,
              }}
            />
          </div>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 9 }}>
        <SecondaryButton onClick={onClose} disabled={busy}>
          Cancel
        </SecondaryButton>
        <PrimaryButton onClick={onSubmit} disabled={busy}>
          {busy ? "Saving…" : editing ? "Save Changes" : "Award Star"}
        </PrimaryButton>
      </div>
    </div>
  </ModalOverlay>
);
StarFormModal.propTypes = {
  editing: PropTypes.object,
  form: PropTypes.object.isRequired,
  setForm: PropTypes.func.isRequired,
  employees: PropTypes.array.isRequired,
  badges: PropTypes.array.isRequired,
  busy: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onPhotoChange: PropTypes.func.isRequired,
};

export default StarOfMonthManagement;
